/**
 * The one video class the web sends today — the exercise demo film — and the numbers every film is
 * held to before a byte of it leaves the browser.
 *
 * Mirrors the phone's `src/shared/media/videoClasses.ts` (`demo`), which in turn mirrors the
 * backend's `MediaClassMap.cs` (`DemoVideo`): `video/mp4` only, at most 50 MiB and 120 seconds at
 * the slot (`VideoSlotAllocator`). The backend refuses anything over those caps with a Problem
 * Details `code`, so the browser checks them first and says why in the zone, instead of letting the
 * trainer meet the refusal after the wizard has closed.
 *
 * The phone encodes to about 3 Mbps at 720p (long edge 1280), which is also what the server's own
 * x264 fallback produces; a two-minute film at that rate is about 47 MB, under the cap with room for
 * the audio.
 */

export const VIDEO_CLASS = {
  /** The slot's duration cap, in whole seconds (`durationSeconds` is an integer on the wire). */
  maxDurationSeconds: 120,
  /** The longest edge of the encoded picture, never scaled up. 1280 × 720 for a 16:9 film. */
  maxEdge: 1280,
  /** The video bitrate handed to the encoder, bits per second. */
  videoBitrate: 3_000_000,
  /** The AAC bitrate, bits per second. */
  audioBitrate: 128_000,
  /**
   * A sanity gate on the RAW file, before anything is decoded: 1.5 GiB, the phone's own `demo`
   * preflight. Not the delivery cap — a long 4K clip is expected to be far over 50 MiB before it
   * is compressed.
   */
  preflightMaxBytes: 1_610_612_736,
  /** The slot's byte cap (`52_428_800`), applied to what is actually SENT. */
  maxDeliveryBytes: 52_428_800,
  /** The one type the slot accepts. */
  contentType: 'video/mp4',
} as const;
